import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface DailyReward {
  day: number
  gems: number
  xp: number
  isMilestone: boolean
}

// 7-day reward cycle
export const DAILY_REWARDS: DailyReward[] = [
  { day: 1, gems: 10, xp: 50, isMilestone: false },
  { day: 2, gems: 15, xp: 75, isMilestone: false },
  { day: 3, gems: 20, xp: 100, isMilestone: false },
  { day: 4, gems: 25, xp: 125, isMilestone: false },
  { day: 5, gems: 30, xp: 150, isMilestone: false },
  { day: 6, gems: 40, xp: 200, isMilestone: false },
  { day: 7, gems: 100, xp: 500, isMilestone: true }, // Weekly milestone!
]

interface DailyRewardsState {
  currentStreak: number
  lastClaimDate: string | null
  totalDaysClaimed: number

  // Actions
  canClaimToday: () => boolean
  claimDailyReward: () => DailyReward | null
  getCurrentReward: () => DailyReward
  getStreakDay: () => number
  resetStreak: () => void
}

const getDateString = (date: Date = new Date()): string => {
  return date.toISOString().split('T')[0]
}

const isConsecutiveDay = (lastDate: string, currentDate: string): boolean => {
  const last = new Date(lastDate)
  const current = new Date(currentDate)

  // Set both to midnight for comparison
  last.setHours(0, 0, 0, 0)
  current.setHours(0, 0, 0, 0)

  const diffTime = current.getTime() - last.getTime()
  const diffDays = diffTime / (1000 * 60 * 60 * 24)

  return diffDays === 1
}

const isSameDay = (date1: string, date2: string): boolean => {
  return date1 === date2
}

export const useDailyRewardsStore = create<DailyRewardsState>()(
  persist(
    (set, get) => ({
      currentStreak: 0,
      lastClaimDate: null,
      totalDaysClaimed: 0,

      canClaimToday: () => {
        const { lastClaimDate } = get()
        const today = getDateString()

        // If never claimed, can claim
        if (!lastClaimDate) return true

        // If already claimed today, cannot claim
        if (isSameDay(lastClaimDate, today)) return false

        return true
      },

      getCurrentReward: () => {
        const { currentStreak, lastClaimDate } = get()
        const today = getDateString()

        let effectiveStreak = currentStreak

        // If last claim was more than 1 day ago (and not today), streak would reset
        if (lastClaimDate && !isSameDay(lastClaimDate, today) && !isConsecutiveDay(lastClaimDate, today)) {
          effectiveStreak = 0
        }

        // Get day in 7-day cycle (1-7)
        const dayInCycle = (effectiveStreak % 7) + 1
        return DAILY_REWARDS[dayInCycle - 1]
      },

      getStreakDay: () => {
        const { currentStreak, lastClaimDate } = get()
        const today = getDateString()

        // If last claim was more than 1 day ago, streak would reset
        if (lastClaimDate && !isSameDay(lastClaimDate, today) && !isConsecutiveDay(lastClaimDate, today)) {
          return 1 // Would be day 1 of new streak
        }

        // Return next day in cycle (1-7)
        return (currentStreak % 7) + 1
      },

      claimDailyReward: () => {
        const { canClaimToday, lastClaimDate, currentStreak, totalDaysClaimed } = get()

        if (!canClaimToday()) return null

        const today = getDateString()
        let newStreak = currentStreak

        // Check if streak continues or resets
        if (lastClaimDate) {
          if (isConsecutiveDay(lastClaimDate, today)) {
            // Consecutive day, continue streak
            newStreak = currentStreak + 1
          } else if (!isSameDay(lastClaimDate, today)) {
            // Missed a day, reset streak
            newStreak = 1
          }
        } else {
          // First ever claim
          newStreak = 1
        }

        // Get reward for current day in cycle
        const dayInCycle = ((newStreak - 1) % 7) + 1
        const reward = DAILY_REWARDS[dayInCycle - 1]

        set({
          currentStreak: newStreak,
          lastClaimDate: today,
          totalDaysClaimed: totalDaysClaimed + 1,
        })

        return reward
      },

      resetStreak: () => set({
        currentStreak: 0,
        lastClaimDate: null,
      }),
    }),
    {
      name: 'waifudo-daily-rewards',
    }
  )
)
